import React, { useState } from "react";
import { Subscription } from "react-apollo";
import { useMutation, useQuery } from "@apollo/react-hooks";
import styled from "styled-components";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import translateErrorMessage from "utils/translateErrorMessage";
import { httpClient } from "../../../settings/apolloClient";
import PENDING_LOCATION_REFILL_REQUESTS from "../graphql/queries/pendingLocationRefillRequests";
import SUBSCRIBE_TO_LOCATION_REFILL_REQUEST from "../graphql/subscriptions/subscribeToLocationRefillRequest";
import {
  APPROVE_LOCATION_REFILL_REQUEST,
  REJECT_LOCATION_REFILL_REQUEST,
} from "../graphql/mutations/locationRefillRequestMutations";
import RefillEntryAlert from "./RefillEntryAlert";
import RefillEntryModal from "./RefillEntryModal";

const Container = styled.div`
  top: ${({ hasManualOperation }) => (hasManualOperation ? "175px" : "55px")};
  left: 80px;
  position: fixed;
  z-index: 1028;
  height: 0px;

  @media screen and (max-width: 991px) {
    left: 0px;
  }
`;

/**
 * Abastecimento detectado, esperando virar entrada de estoque.
 *
 * Nunca abre sozinho, ao contrário da operação manual e da requisição de carga:
 * o nível do silo no painel já lê o sensor e está correto desde o abastecimento,
 * então o que falta é só a contabilidade e não há urgência. Operação manual
 * continua na frente, e o aviso desce para não cobrir o dela.
 */
const RefillEntry = ({ hasManualOperation, snackbarContext }) => {
  const [openRequestId, setOpenRequestId] = useState(null);

  // O abastecimento é criado pelo controller a qualquer momento (caminhão
  // enchendo o silo), fora do ciclo de carga que mantém os outros avisos vivos.
  // Sem atualização ao vivo o alerta só aparecia após um refresh; a subscription
  // abaixo faz o refetch quando um abastecimento é detectado ou resolvido.
  const { data, refetch } = useQuery(PENDING_LOCATION_REFILL_REQUESTS, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const [approveRequest] = useMutation(APPROVE_LOCATION_REFILL_REQUEST, { client: httpClient });
  const [rejectRequest] = useMutation(REJECT_LOCATION_REFILL_REQUEST, { client: httpClient });

  const {
    pendingLocationRefillRequests: requests,
    suppliers,
    inventoryItems,
  } = data || {};

  const pending = requests || [];
  const openRequest = pending.find(request => request.id === openRequestId) || null;

  const close = () => setOpenRequestId(null);

  const onDone = (message) => {
    snackbarContext.setNotificationTimeOut(Color.success, message);
    close();
    refetch();
  };

  const onFail = (error) => {
    snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
  };

  const onApprove = (id, params) => approveRequest({ variables: { id, params } })
    .then(() => onDone("Entrada de estoque registrada."))
    .catch(onFail);

  const onReject = (id, rejectionReason) => rejectRequest({
    variables: { id, rejectionReason: rejectionReason || null },
  })
    .then(() => onDone("Abastecimento descartado."))
    .catch(onFail);

  return (
    <React.Fragment>
      {/* Sempre montado: precisa ouvir mesmo sem pendências para trazer o
          primeiro abastecimento sem refresh. */}
      <Subscription
        subscription={SUBSCRIBE_TO_LOCATION_REFILL_REQUEST}
        onSubscriptionData={() => refetch()}
      />

      {pending.length > 0 && (
        <React.Fragment>
          <Container hasManualOperation={hasManualOperation}>
            <RefillEntryAlert
              amount={pending.length}
              onOpen={() => setOpenRequestId(pending[0].id)}
            />
          </Container>

          <RefillEntryModal
            request={openRequest}
            suppliers={suppliers}
            inventoryItems={inventoryItems}
            onClose={close}
            onApprove={onApprove}
            onReject={onReject}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default withSnackbarConsumer(RefillEntry);
